import { reverseLookupMapForEnum } from './messages/util';

export enum CardInfoEntryIndicator {
  Keyed,
  Swiped,
  TapMSD,
  TapEMV,
  BarCode,
  EMVContact,
  QRCode
}

export enum CardInReader {
  NoCardInReader,
  // eslint-disable-next-line no-shadow
  CardInReader
}

export const CardInReaderLookupMap = reverseLookupMapForEnum(CardInReader);

// card type mappings
// @todo add more card types
export enum CardType {
  MASTERCARD = 49,
  VISA = 50,
  AMEX = 51,
  DISCOVER = 52,
  GIFT_CARD = 80
}

export enum Cvv2Indicator {
  NotProvided,
  Present,
  Ineligible,
  NotOnCard = 9
}

/**
 * ISO-4217 currency codes - letter to numeric mapping
 * @todo add more currency codes
 * @see {@link https://www.iso.org/iso-4217-currency-codes.html}
 */
export enum CurrencyCode {
  CAD = '124',
  USD = '840',
  GBP = '826',
  CHF = '756',
  ZAR = '710'
}

export enum DeviceStatusRequestIndicator {
  // No action performed
  NoAction,
  // request current pin pad status only
  // no "check" is performed
  CurrentStatusNoCheck,
  // request current pin pad status and
  // perform a "check"
  CurrentStatusWithCheck,
  // request current pin pad status with
  // device serial number
  CurrentStatusWithSerial,
  // Cancel Card entry - this is only
  // supported on 'selected pin pads'
  // use with caution
  CancelCardEntry,
  // Check for card in reader
  CardInReaderCheck = 8
}

/**
 * This indicator must be provided to utilize
 * certain actions on Device Status requests.
 */
export enum DeviceTypeActionIndicator {
  NoDeviceSelected,
  E355 = 2,
  MxVxOrIngenico = 7
}

export enum DuplicateLevelCheckIndicator {
  NoCheck,
  NoDuplicateAllowedInCurrentOpenBatch,
  DuplicateAllowedInCurrentOpenBatchOnFirstTransaction
}

export enum ECommerceGoodsIndicator {
  Digital,
  Physical
}

export enum Encoding {
  ascii = 'ascii',
  utf8 = 'utf-8'
}

export enum FieldJustification {
  left = 'left',
  right = 'right'
}

export enum FieldType {
  alphaNumeric = 'alphaNumeric',
  base64 = 'base64',
  fixed = 'fixed',
  float = 'float',
  numeric = 'numeric',
  reserved = 'reserved'
}

export enum GiftCardEntryMethodIndicator {
  Scanned = 'B',
  Swiped = 'M'
}

export enum HostStatus {
  NotConnected,
  Connected,
  ConnectionAttemptSuccess,
  ConnectionAttemptFail,
  InformationNotAvailable = 99
}

export const HostStatusLookupMap = reverseLookupMapForEnum(HostStatus);

export enum HostGiftStatus {
  NotConnected,
  Connected,
  ConnectionAttemptSuccess,
  ConnectionAttemptFail,
  InformationNotAvailable = 99
}

export const HostGiftStatusLookupMap = HostStatusLookupMap;

export enum HostStatusRequestIndicator {
  NoAction,
  /**
   * Returns current status of connection,
   * without testing the connection
   */
  CurrentStatusNoCheck,
  /**
   * Performs a check of the current
   * connection to host and returns the status
   */
  CurrentStatusWithCheck
}

export enum InstallmentIndicator {
  Installment = 'I',
  Recurring = '1'
}

export enum LastTransactionStatus {
  Approved = 'A',
  ApprovedOffline = 'F',
  Declined = 'D',
  Error = 'E',
  NoData = 'N',
  PreApproved = 'I',
  Reversed = 'R'
}

export const LastTransactionStatusLookupMap = reverseLookupMapForEnum(LastTransactionStatus);

/**
 * when the card data is provided by the user
 * rather than obtained from a swipe we must provide
 * a payment type if possible (or Unspecified if unknown)
 */
export enum PaymentTypeIndicator {
  Unspecified,
  MasterCard,
  Visa,
  Amex,
  Discover,
  Diners = 6,
  JCB,
  PrivateLabel,
  VisaDebit = 20,
  MasterCardUSDebit = 22,
  DiscoverUSDebit = 24,
  VisaUSDebit,
  VisaChaseNet,
  GiftCard = 32,
  LoyaltyCard,
  AliPay,
  WeChat,
  FleetCard = 42
}

/**
 * Pin Pad Action1 actions are only
 * available if sending the Device
 * Type indicator 2, with the exception
 * of ResetDeviceType7 which can only be
 * used with Device Type indicator 7
 * (these actions may not work on all devices)
 */
export enum PinPadAction1Indicator {
  NoAction,
  /**
   * Used to enable PinPad Action2 Flag field.
   */
  ChangeConfiguration,
  ResetDevice,
  ResetDeviceType7 = 'R'
}

/**
 * Pin pad Action2 can be used to modify
 * configuration values if the value of
 * Pin Pad Action1 is "ChangeConfiguration"
 *
 * These actions are only supported for devices
 * with mounted barcode scanners, @see PinPadAction3IndicatorContinuousMode
 * and @see PinPadAction3IndicatorAutoBeepMode
 */
export enum PinPadAction2Indicator {
  NoAction,
  ContinuousMode,
  AutoBeepMode,
  SymbologyParameters
}

/**
 * These indicators can be used with PinPadAction2Indicator
 * in ContinuousMode
 */
export enum PinPadAction3IndicatorContinuousMode {
  Disable = '00',
  Enable = '01'
}

/**
 * These indicators can be used with
 * PinPadAction2Indicator.AutoBeepMode
 */
export enum PinPadAction3IndicatorAutoBeepMode {
  Disable = '00',
  Enable = '01',
  ErrorBeep = '02',
  ScanBeep = '03'
}

/**
 * these Indicators can be used with
 * PinPadAction2Indicator.SymbologyParameters
 */
export enum PinPadAction3IndicatorSymbologyParameters {
  /**
   * Default
   */
  Code128
  /**
   * @todo define other symbology parameters as needed
   */
}

/**
 * used in conjunction with
 * PinPadAction3IndicatorSymbologyParameters
 * to enable or disable symbology
 */
export enum PinPadAction4Indicator {
  Disable,
  Enable
}

export enum PinPadStatus {
  NotPresent,
  OK,
  Error,
  Busy,
  NotConnected,
  CannotConnect,
  InformationNotAvailable = 99
}

export const PinPadStatusLookupMap = reverseLookupMapForEnum(PinPadStatus);

// delimiter characters used in responses
export enum ResponseDelimiter {
  ACK = '\u0006',
  Colon = ':',
  CRLF = '\r\n',
  ETX = '\u0003',
  FS = '\u001c',
  NAK = '\u0015',
  STX = '\u0002'
}

// response keys to readable keys mapping
export enum ResponseKey {
  AMT = 'amountRequested',
  AUT = 'authorizationNumber',
  AVS = 'avsResponse',
  BIN = 'cardBin',
  CBK = 'cashBackAmount',
  CFV = 'ctlFirmwareVersion',
  CLV = 'coreLibraryVersion',
  CRN = 'cardNumber',
  CRT = 'creditType',
  CVD = 'cvdResponse',
  DAT = 'date',
  EHL = 'echoLotteryStatus',
  EKV = 'emvKernelVersion',
  EXP = 'expirationDate',
  HGS = 'hostGiftStatus',
  HSS = 'hostStatus',
  INV = 'invoiceNumber',
  LNG = 'language',
  LTS = 'lastTransactionStatus',
  MOD = 'deviceModelName',
  MRC = 'merchantId',
  OPR = 'operatorId',
  PAV = 'applicationVersion',
  PPN = 'pinPadName',
  PPS = 'pinPadStatus',
  PSN = 'pinPadSerialNumber',
  PYT = 'paymentType',
  RBA = 'remainingBalance',
  RCP = 'receiptLine',
  REF = 'transactionNumber',
  RES = 'result',
  ROV = 'romOsVersion',
  SEQ = 'sequenceNumber',
  SWP = 'inputMethod',
  RRT = 'remainingDeviceAutoRebootTime',
  TAT = 'totalTransactionAmount',
  TIM = 'time',
  TIP = 'tipAmount',
  TOC = 'fallbackToken',
  TOK = 'token',
  TRM = 'terminalId',
  TRS = 'terminalStatus',
  TYP = 'transactionType',
  VRC = 'cdcaMDllVersion',
  VRD = 'hostDriverVersion',
  VRM = 'multiExeVersion'
}

// response status codes
// @todo add more status codes
// there are a lot
export enum ResponseStatusCode {
  Declined = '0[48]',
  Approved = '1[49]',
  ApprovedOffline = '2[50]',
  CardEntryCanceled = 'h[104]',
  CardEntryTimeOut = 'i[105]',
  PinPadInputError = 'G[71]',
  InvalidCard = 'F[70]',
  InvalidCardNumber = 'H[72]',
  TimeOut = 'J[74]',
  TransactionNotSupported = 'Z[90]'
}

// reverse-lookup map for ResponseStatusCode
export const ResponseStatusCodeLookupMap = reverseLookupMapForEnum(ResponseStatusCode);

export enum ReturnVersionRequestIndicator {
  /**
   * Do not return version
   */
  NoAction,
  /**
   * Return version for given terminal ID
   */
  VersionForTerminal
}

export enum LastTransactionStatusRequestIndicator {
  NoAction,
  /**
   * Return Last Transaction status only
   */
  StatusOnly,
  /**
   * Return Last Transaction status and receipt
   */
  StatusAndReceipt
}

export enum StopProcessingRequestIndicator {
  NoAction,
  /**
   * Stops processing transactions on the given
   * terminal ID
   */
  StopProcessing,
  /**
   * Stop listening for transactions on the given
   * terminal ID
   */
  StopListening,
  /***
   * Start Listening for transactions again on the
   * given terminal ID
   */
  StartListening
}

export enum TaxIndicator {
  NotProvided,
  TaxIncluded,
  NonTaxableTransaction
}

/**
 * The status indicator sent in the
 * DeviceStatus response string
 */
export enum TerminalStatus {
  NotActive,
  IdleNoPinPad,
  Processing,
  IdlePinPadError,
  IdlePinPadOk,
  CheckingPinPad,
  TerminalIdNotFound = 99
}

export const TerminalStatusLookupMap = reverseLookupMapForEnum(TerminalStatus);

/**
 * Used for payment requests that are not
 * using the ExpandedRequest new format.
 * Expanded request messages are always
 * Subtype 8.
 */
export enum TransactionSubtype {
  OldFormat = '0',
  CurrentFormat = '1',
  OldFormatWithCompanyName = 'P',
  CurrentFormatWithCompanyName = 'R',
  OldFormatRemotePinPadRequest = '2',
  CurrentFormatRemotePinPadRequest = '3',
  GiftCardFormat = '4',
  VerifiedVisaFormat = '5',
  EmexTransactionFormat = '6',
  ExpandedFormat = '7',
  ExpandedRequestSubtype = '8'
}

export enum TransactionType {
  // Credit Card Types
  // standard purchase request (Default)
  CreditCardPurchase = '00',
  // pre-authorization request
  CreditCardPreAuth = '10',
  // store and forward
  CreditCardPurchaseSAF = '20',
  // purchase correction
  CreditCardPurchaseCorrection = '30',
  // credit card refund
  CreditCardRefund = '40',
  // credit card refund correction
  CreditCardRefundCorrection = '50',
  // Debit Card Types
  // standard purchase
  DebitCardPurchase = '01',
  // debit purchase correction
  DebitCardPurchaseCorrection = '31',
  // debit refund
  DebitCardRefund = '41',
  // debit refund correction
  DebitCardRefundCorrection = '51',
  // device status
  DeviceStatus = 'XO',
  // gift card - no partial redemption, balance
  // must be greater than amount
  GiftCardRedemptionNoNSF = '83',
  // gift card - redemption allowed even if requested
  // amount is greater than balance - must charge another
  // payment method for balance due
  GiftCardRedemption = '33',
  // loyalty card
  LoyaltyCardType = '04',
  // unknown card type
  UnknownCardType = '0U',
  // Purchase - Prompt User for Card Type
  GeneralCardType = '0G'
}
